import { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  InputAdornment,
  Link,
} from '@mui/material';
import { Eye, EyeOff, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from './auth';
import { api } from './api';
import { useAppSettings } from './SettingsContext';
import { BRANDS } from './appSettings';
import './login.css';

const TAGLINES = [
  {
    t: 'Your people, one platform',
    s: 'Profiles, org structure, and team directories in a single, beautiful hub.',
  },
  {
    t: 'Attendance & leave, simplified',
    s: 'Clock-ins, approvals, and balances — tracked live, the moment they happen.',
  },
  {
    t: 'Applicant tracking, end to end',
    s: 'Source, screen, interview, and onboard — one seamless recruitment pipeline.',
  },
  {
    t: 'Performance that grows people',
    s: 'Goals, reviews, 360° feedback, and calibration in one performance hub.',
  },
  {
    t: 'Payroll, tax & insights',
    s: 'Payslips, benefits, and income tax — accurate and actionable every cycle.',
  },
];

// Brand-driven via CSS vars (set by applyAppSettings) so the login matches the
// selected brand with zero pink under DIH.
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    backgroundColor: '#FAFAFB',
    transition: 'box-shadow .2s, background-color .2s',
    '& fieldset': { borderColor: '#E5E7EB' },
    '&:hover fieldset': { borderColor: '#D0D5DD' },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      boxShadow: '0 0 0 4px rgba(var(--primary-rgb),0.10)',
    },
    '&.Mui-focused fieldset': { borderColor: 'var(--primary)', borderWidth: 1.5 },
  },
};

const btnSx = {
  borderRadius: 2.5,
  py: 1.35,
  textTransform: 'none',
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: '0.01em',
  background: 'linear-gradient(135deg,var(--primary) 0%,var(--primary-dark) 100%)',
  boxShadow: '0 12px 26px rgba(var(--primary-rgb),0.32)',
  transition: 'transform .15s, box-shadow .2s, filter .2s',
  '&:hover': {
    background: 'linear-gradient(135deg,var(--primary) 0%,var(--primary-darkest) 100%)',
    boxShadow: '0 16px 32px rgba(var(--primary-rgb),0.4)',
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': {
    background: '#E5E7EB',
    color: '#9DA4AE',
    boxShadow: 'none',
  },
};

export default function Login() {
  const { login } = useAuth();
  const { settings, updateSettings } = useAppSettings();
  const brand = settings.brand === 'dih' ? 'dih' : 'augment';
  const brandConfig = BRANDS[brand];
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [tag, setTag] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setTag((t) => (t + 1) % TAGLINES.length),
      5000,
    );
    return () => clearInterval(id);
  }, []);

  const continueWithEmail = (e) => {
    e?.preventDefault();
    setErr('');
    if (!email.trim()) {
      setErr('Please enter your email address');
      return;
    }
    setStep('password');
  };

  const submit = async (e) => {
    e?.preventDefault();
    setErr('');
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (ex) {
      setErr(ex.message || 'Login failed');
      setBusy(false);
    }
  };

  const backToEmail = () => {
    setStep('email');
    setPassword('');
    setErr('');
    setNotice('');
  };
  // Forgot password: the email is already known (entered on the first step), so
  // request a reset link for it. Response is intentionally generic.
  const forgot = async () => {
    setErr('');
    try {
      await api.forgotPassword(email.trim());
    } catch {
      /* ignore — generic response either way */
    }
    setNotice("If that account exists, we've emailed a password reset link. Check your inbox.");
  };
  const t = TAGLINES[tag];

  return (
    <div className="lx-root">
      {/* Brand switch — choose Augment or the DIH client theme before signing in */}
      <div className="lx-brand-toggle" role="tablist" aria-label="Brand theme">
        {Object.entries(BRANDS).map(([key, b]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={brand === key}
            className={`lx-brand-seg${brand === key ? ' on' : ''}`}
            onClick={() => updateSettings({ brand: key })}
          >
            {b.label}
          </button>
        ))}
      </div>

      {/* ---------- Left: form ---------- */}
      <div className="lx-form-panel">
        <div className="lx-form-inner">
          <img
            className={`lx-logo${brand === 'dih' ? ' dih' : ''}`}
            src={brandConfig.logo}
            alt={brandConfig.name}
          />

          <div className="lx-eyebrow">
            {step === 'email' ? `Welcome to ${brandConfig.name}` : 'Almost there'}
          </div>
          {/* <h1 className="lx-title">{step === 'email' ? 'Welcome back' : 'Enter your password'}</h1> */}
          <p className="lx-sub">
            {step === 'email'
              ? 'Sign in to your workspace to continue.'
              : `Signing in as ${email}`}
          </p>

          <form onSubmit={step === 'email' ? continueWithEmail : submit}>
            <Stack spacing={2}>
              {step === 'email' ? (
                <TextField
                  fullWidth
                  type="email"
                  label="Work email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                  sx={inputSx}
                />
              ) : (
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                  required
                  sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                          aria-label={
                            showPassword ? 'Hide password' : 'Show password'
                          }
                          sx={{ color: 'text.secondary' }}
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: -0.5 }}
              >
                {step === 'email' ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        size="small"
                        sx={{
                          color: '#D0D5DD',
                          '&.Mui-checked': { color: 'var(--primary)' },
                        }}
                      />
                    }
                    label="Remember me"
                    sx={{
                      color: 'text.secondary',
                      '& .MuiFormControlLabel-label': { fontSize: 14 },
                    }}
                  />
                ) : (
                  <span />
                )}
                {step === 'password' && (
                  <Link
                    component="button"
                    type="button"
                    onClick={(e) => { e.preventDefault(); forgot(); }}
                    underline="hover"
                    sx={{ fontSize: 14, color: 'var(--primary)', fontWeight: 600 }}
                  >
                    Forgot password?
                  </Link>
                )}
              </Stack>

              {err && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {err}
                </Alert>
              )}
              {notice && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  {notice}
                </Alert>
              )}

              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={busy}
                disableElevation
                sx={btnSx}
              >
                {busy ? (
                  <>
                    <CircularProgress
                      size={18}
                      color="inherit"
                      sx={{ mr: 1 }}
                    />{' '}
                    Signing in…
                  </>
                ) : step === 'email' ? (
                  <>
                    Continue <ArrowRight size={18} style={{ marginLeft: 8 }} />
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>

              {step === 'password' && (
                <Button
                  fullWidth
                  size="large"
                  type="button"
                  variant="text"
                  onClick={backToEmail}
                  sx={{
                    textTransform: 'none',
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  Use a different email
                </Button>
              )}
            </Stack>
          </form>
        </div>

        <div className="lx-foot">
          © {new Date().getFullYear()}{' '}
          {brand === 'dih' ? 'Digital Infrastructure Holdings' : 'Augment Group, Inc.'} ·{' '}
          <a href="#a">Privacy</a> · <a href="#b">Terms</a>
        </div>
      </div>

      {/* ---------- Right: flowing aurora showcase ---------- */}
      <div className="lx-stage">
        <div
          className="lx-photo"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/assets/login/login-bg.jpg)`,
          }}
        />
        <div className="lx-overlay" />

        <div className="lx-brand">
          <span className="dot">
            <Sparkles size={17} color="#fff" />
          </span>
          {brandConfig.name}
        </div>

        <div className="lx-headline">
          <div key={tag} className="lx-fade">
            <h2>{t.t}</h2>
            <p>{t.s}</p>
          </div>
          <div className="lx-dots">
            {TAGLINES.map((_, i) => (
              <span key={i} className={i === tag ? 'on' : ''} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
